/**
 * HangarMax `Item` → PlaneMAX aircraft/allocation or obstacle.
 *
 * Classification:
 *  - `aircraft`                                     → `aircraft` + `allocations`
 *  - vehicle/equipment/structural/custom, PROCEDURAL → `obstacles` (needs a host unit)
 *  - vehicle/equipment/structural/custom, GLTF       → skipped, preserved, retried each resync
 *  - `annotation`                                    → skipped, preserved
 *
 * Skipping is never data loss: the whole item lands in
 * `hangarmax_import_links.raw_source`, and every resync re-runs this mapping, so
 * items become importable the moment PlaneMAX grows the matching capability.
 */

import type { ObstacleShape, ObstacleType } from '@/types/obstacle'
import {
  areaLocalToObstacleUnitLocal,
  isInsideAreaFootprint,
  metersToFeet,
  obstacleRotationFromHm,
  sceneToAreaLocalFeet,
  type AreaFrame,
} from './transform'
import type { HmCatalogEntry, HmItem, PlannedHostRef } from './types'

// ----------------------------------------------------------------------------
// Host resolution
// ----------------------------------------------------------------------------

export interface HostUnitFrame {
  index: number
  /** Feet, in the PlaneMAX unit-offset convention. */
  offsetX: number
  offsetY: number
  widthFt: number
  depthFt: number
}

export interface HostBuildingFrame {
  buildingSourceId: string
  frame: AreaFrame
  units: HostUnitFrame[]
  /** `depth/2` for templates whose orchestrator centres Z (double-row, nested-T). */
  centerZShiftFt: number
}

export interface HostResolution {
  ref: PlannedHostRef
  building: HostBuildingFrame
  unit: HostUnitFrame
  /** Building-local feet. */
  local: { x: number; z: number }
}

/**
 * Find the building/unit whose footprint contains a scene point.
 *
 * `Item.parent_building` is only a soft tag in HangarMax (coordinates are always
 * scene-root), so geometry decides and the tag is used only to break ties.
 */
export function resolveHost(
  point: [number, number, number],
  hosts: HostBuildingFrame[],
  parentBuildingTag: string | null | undefined
): HostResolution | null {
  const hits: HostResolution[] = []

  for (const building of hosts) {
    const local = sceneToAreaLocalFeet(point, building.frame)
    if (!isInsideAreaFootprint(local, building.frame)) continue

    for (const unit of building.units) {
      const unitCenterX = unit.offsetX + unit.widthFt / 2 - building.frame.widthFt / 2
      const unitCenterZ = -unit.offsetY + building.centerZShiftFt
      if (
        Math.abs(local.x - unitCenterX) <= unit.widthFt / 2 &&
        Math.abs(local.z - unitCenterZ) <= unit.depthFt / 2
      ) {
        hits.push({
          ref: { buildingSourceId: building.buildingSourceId, unitIndex: unit.index },
          building,
          unit,
          local,
        })
      }
    }
  }

  if (hits.length === 0) return null
  if (hits.length === 1) return hits[0]

  return (
    hits.find((h) => h.ref.buildingSourceId === parentBuildingTag) ?? hits[0]
  )
}

// ----------------------------------------------------------------------------
// Dimensions
// ----------------------------------------------------------------------------

export interface ItemDimensionsFt {
  widthFt: number | null
  lengthFt: number | null
  heightFt: number | null
}

/** Per-item overrides win over the catalog entry, matching HangarMax rendering. */
export function itemDimensionsFeet(
  item: HmItem,
  entry: HmCatalogEntry | undefined
): ItemDimensionsFt {
  const widthM = item.overrides?.width_m ?? entry?.widthM ?? null
  const lengthM = item.overrides?.depth_m ?? entry?.lengthM ?? null
  const heightM = item.overrides?.height_m ?? entry?.heightM ?? null

  return {
    widthFt: widthM == null ? null : round2(metersToFeet(widthM)),
    lengthFt: lengthM == null ? null : round2(metersToFeet(lengthM)),
    heightFt: heightM == null ? null : round2(metersToFeet(heightM)),
  }
}

export function itemDisplayLabel(
  item: HmItem,
  entry: HmCatalogEntry | undefined,
  index: number
): string {
  return (
    item.label?.trim() ||
    entry?.name?.trim() ||
    (item.catalog_id ? `Catalog item ${item.catalog_id.slice(0, 8)}` : `Item ${index + 1}`)
  )
}

// ----------------------------------------------------------------------------
// Classification
// ----------------------------------------------------------------------------

export type ItemClass =
  | { kind: 'aircraft' }
  | { kind: 'obstacle'; shape: ObstacleShape; obstacleType: ObstacleType; color: string }
  | { kind: 'skip'; reason: string }

export function classifyItem(
  item: HmItem,
  entry: HmCatalogEntry | undefined
): ItemClass {
  if (item.type === 'aircraft') return { kind: 'aircraft' }

  if (item.type === 'annotation') {
    return { kind: 'skip', reason: 'Annotations are not supported in PlaneMAX yet.' }
  }

  const proceduralShape = item.overrides?.procedural_shape ?? entry?.proceduralShape ?? null
  const isProcedural = Boolean(entry?.isProcedural || item.overrides?.procedural_shape)

  if (!isProcedural) {
    // GLTF props have no PlaneMAX equivalent: obstacles render as instanced
    // boxes/cylinders only. Preserved and retried once obstacle models land.
    const name = entry?.name ? `: ${entry.name}` : ''
    return {
      kind: 'skip',
      reason: `No matching PlaneMAX model${name} — PlaneMAX obstacles render as boxes and cylinders only.`,
    }
  }

  const shape: ObstacleShape = proceduralShape === 'cylinder' ? 'circle' : 'rectangle'
  const color =
    item.overrides?.color ?? entry?.proceduralColor ?? DEFAULT_OBSTACLE_COLOR

  return {
    kind: 'obstacle',
    shape,
    obstacleType: obstacleTypeFor(item, entry),
    color,
  }
}

const DEFAULT_OBSTACLE_COLOR = '#808080'

/**
 * Map a HangarMax item to the closest `obstacles.obstacle_type`.
 * The category gives a coarse answer; the display name refines it.
 */
export function obstacleTypeFor(
  item: HmItem,
  entry: HmCatalogEntry | undefined
): ObstacleType {
  const name = (entry?.name ?? item.label ?? '').toLowerCase()

  if (/column|pillar|post/.test(name)) return 'column'
  if (/firewall/.test(name)) return 'firewall'
  if (/office/.test(name)) return 'office'
  if (/bathroom|restroom|toilet|lavatory/.test(name)) return 'restroom'
  if (/storage|parts|tool|crib|shelf|locker/.test(name)) return 'storage'
  if (/barrier|cone|fence|rail/.test(name)) return 'barrier'
  if (/wall/.test(name)) return 'wall'
  if (/utility|electrical|hvac|compressor/.test(name)) return 'utility'

  switch (item.type) {
    case 'vehicle':
      return 'vehicle'
    case 'equipment':
      return 'equipment'
    case 'structural':
      return 'other'
    default:
      return 'other'
  }
}

// ----------------------------------------------------------------------------
// Obstacle geometry
// ----------------------------------------------------------------------------

export interface ObstacleGeometry {
  positionX: number
  positionY: number
  rotation: number
  widthFt: number | null
  depthFt: number | null
  radiusFt: number | null
  heightFt: number
}

/** Minimum height PlaneMAX accepts (`obstacles.valid_height` requires > 0). */
const MIN_OBSTACLE_HEIGHT_FT = 0.1

export function obstacleGeometry(params: {
  host: HostResolution
  dims: ItemDimensionsFt
  shape: ObstacleShape
  hmRotationDeg: number
}): ObstacleGeometry {
  const { host, dims, shape, hmRotationDeg } = params

  const { positionX, positionY } = areaLocalToObstacleUnitLocal(
    host.local,
    { offsetX: host.unit.offsetX, offsetY: host.unit.offsetY },
    host.building.frame.widthFt,
    host.building.centerZShiftFt
  )

  const widthFt = dims.widthFt ?? 1
  const depthFt = dims.lengthFt ?? widthFt

  return {
    positionX: round2(positionX),
    positionY: round2(positionY),
    rotation: round2(
      obstacleRotationFromHm(hmRotationDeg, host.building.frame.rotationDeg)
    ),
    widthFt: shape === 'rectangle' ? round2(widthFt) : null,
    depthFt: shape === 'rectangle' ? round2(depthFt) : null,
    radiusFt: shape === 'circle' ? round2(Math.max(widthFt, depthFt) / 2) : null,
    heightFt: round2(Math.max(dims.heightFt ?? MIN_OBSTACLE_HEIGHT_FT, MIN_OBSTACLE_HEIGHT_FT)),
  }
}

function round2(value: number): number {
  return Math.round(value * 100) / 100
}
